import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, CheckCircle2, UserPlus, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUBSCRIBED_KEY = "pti_signup_subscribed";
const DISMISS_KEY = "pti_signup_dismissed_until";
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const SCROLL_TRIGGER = 0.5; // fire after ~50% of the article is scrolled

function suppressed() {
  try {
    if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return true;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() < until;
  } catch { return false; }
}

export function SignupPopup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [enter, setEnter] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const open = useCallback(() => {
    setShow(true);
    requestAnimationFrame(() => setEnter(true));
  }, []);

  useEffect(() => {
    if (user || suppressed()) return undefined; // logged-in visitors + suppressed never see it
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const ratio = window.scrollY / scrollable;
      if (window.scrollY > 200 && ratio >= SCROLL_TRIGGER) {
        window.removeEventListener("scroll", onScroll);
        open();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [user, open]);

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + THIRTY_DAYS)); } catch { /* ignore */ }
    setEnter(false);
    setTimeout(() => setShow(false), 250);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      try { localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch { /* ignore */ }
      setDone(true);
      setTimeout(() => { setEnter(false); setTimeout(() => setShow(false), 250); }, 2200);
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const createAccount = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + THIRTY_DAYS)); } catch { /* ignore */ }
    navigate("/register", { state: { email } });
  };

  if (!show) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 transition-opacity duration-300 ${enter ? "opacity-100" : "opacity-0"}`}
      style={{ backgroundColor: "rgba(17,17,17,0.55)", backdropFilter: "blur(4px)" }}
      onClick={close}
      data-testid="signup-popup"
    >
      <div
        className={`relative w-full max-w-md bg-white shadow-2xl border-t-4 border-t-pti-red transition-all duration-300 ${enter ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <button onClick={close} aria-label="Close" data-testid="signup-popup-close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-pti-black transition-colors">
          <X size={18} />
        </button>

        {done ? (
          <div className="px-8 py-12 text-center" data-testid="signup-popup-success">
            <CheckCircle2 size={44} className="mx-auto text-pti-red" />
            <h3 className="font-display text-2xl font-black mt-4">You're in!</h3>
            <p className="text-sm text-gray-500 mt-2">Thanks for subscribing — the best of PictureThisInk is headed to your inbox.</p>
          </div>
        ) : (
          <div className="px-7 sm:px-8 py-8">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-pti-red">
              <Sparkles size={13} /> Keep reading
            </span>
            <h3 className="font-display text-2xl sm:text-3xl font-black leading-tight mt-2">Enjoying this story?</h3>
            <p className="text-sm text-gray-500 mt-2">Join PictureThisInk for original reporting on tech, AI, design and culture — free, straight to your inbox.</p>

            <form onSubmit={subscribe} className="mt-5 space-y-3">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="signup-popup-email"
                  className="w-full border border-gray-300 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-pti-red"
                />
              </div>
              <button type="submit" disabled={busy} data-testid="signup-popup-subscribe"
                className="w-full bg-pti-red text-white font-bold text-sm py-2.5 hover:bg-pti-black transition-colors disabled:opacity-60">
                {busy ? "Subscribing…" : "Subscribe free"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <span className="h-px flex-1 bg-gray-200" /><span className="text-[11px] text-gray-400 uppercase tracking-wider">or</span><span className="h-px flex-1 bg-gray-200" />
            </div>

            <button onClick={createAccount} data-testid="signup-popup-create-account"
              className="w-full flex items-center justify-center gap-2 border border-pti-black font-bold text-sm py-2.5 hover:bg-gray-50 transition-colors">
              <UserPlus size={15} /> Create a free account
            </button>

            <button onClick={close} data-testid="signup-popup-dismiss"
              className="block mx-auto mt-4 text-xs text-gray-400 hover:text-gray-600 transition-colors">
              No thanks, keep reading
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
